import { useCallback, useRef } from "react";

/**
 * Post-submission easter egg gate.
 *
 * Single roll per submission decides if anything fires and which one.
 * - 40% chance something happens (60% nothing)
 * - Within that 40%, weighted random picks which event:
 *   - Critic: 55 weight (~22% effective per post)
 *   - Astrologer: 30 weight (~12% effective per post)
 *   - Alchemist: 15 weight (~6% effective per post)
 * - Alchemist cooldown override: if outside weekly cooldown, Alchemist gets 100%
 *
 * Future easter eggs just add to the weight pool.
 */

export type PostSubmissionEvent = "critic" | "alchemist" | "astrologer" | null;

const ALCHEMIST_COOLDOWN_KEY = "espy-alchemist-last";
const ONE_WEEK = 7 * 24 * 60 * 60 * 1000;

function isAlchemistOffCooldown(): boolean {
  const last = localStorage.getItem(ALCHEMIST_COOLDOWN_KEY);
  if (!last) return true;
  return Date.now() - parseInt(last, 10) >= ONE_WEEK;
}

function markAlchemistShown(): void {
  localStorage.setItem(ALCHEMIST_COOLDOWN_KEY, String(Date.now()));
}

interface EventWeight {
  event: PostSubmissionEvent;
  weight: number;
}

const EVENT_POOL: EventWeight[] = [
  { event: "critic", weight: 55 },
  { event: "astrologer", weight: 30 },
  { event: "alchemist", weight: 15 },
];

const TRIGGER_CHANCE = 0.4;

function pickWeighted(pool: EventWeight[]): PostSubmissionEvent {
  const total = pool.reduce((sum, e) => sum + e.weight, 0);
  let roll = Math.random() * total;
  for (const entry of pool) {
    roll -= entry.weight;
    if (roll <= 0) return entry.event;
  }
  return pool[pool.length - 1].event;
}

/**
 * Returns a function that rolls once per submission and returns which event to fire.
 * Call it when a moment is successfully published.
 */
export function usePostSubmissionEvent() {
  const lastColorsRef = useRef<string[] | null>(null);

  const roll = useCallback((colors: string[]): PostSubmissionEvent => {
    // Dedupe — don't re-roll for the same colors array reference
    if (lastColorsRef.current === colors) return null;
    lastColorsRef.current = colors;

    // Alchemist cooldown override: guaranteed if off cooldown
    if (isAlchemistOffCooldown()) {
      markAlchemistShown();
      return "alchemist";
    }

    // 40% chance something happens
    if (Math.random() > TRIGGER_CHANCE) return null;

    // Pick from the weighted pool
    const event = pickWeighted(EVENT_POOL);

    // If alchemist was picked, mark cooldown
    if (event === "alchemist") {
      markAlchemistShown();
    }

    return event;
  }, []);

  return roll;
}
